import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, StickyNote, Bookmark as BookmarkIcon } from 'lucide-react';
import type { HighlightColor } from './types';
import { useTranslation } from '../../i18n/useTranslation';

interface QuickActionMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onAddNote?: (noteText: string, color: HighlightColor) => void;
  onAddBookmark?: (location: string | number, note?: string) => Promise<void>;
  currentLocation: string | number;
  currentLocationLabel: string;
  asPanel?: boolean; // If true, render as panel content without bottom sheet wrapper
}

const highlightColors: { color: HighlightColor; name: string }[] = [
  { color: '#FFEB3B' as HighlightColor, name: 'Yellow' },
  { color: '#4CAF50' as HighlightColor, name: 'Green' },
  { color: '#2196F3' as HighlightColor, name: 'Blue' },
  { color: '#E91E63' as HighlightColor, name: 'Pink' },
  { color: '#9C27B0' as HighlightColor, name: 'Purple' },
  { color: '#FF9800' as HighlightColor, name: 'Orange' },
];

export default function QuickActionMenu({
  isOpen,
  onClose,
  onAddNote,
  onAddBookmark,
  currentLocation,
  currentLocationLabel,
  asPanel = false,
}: QuickActionMenuProps) {
  const { t } = useTranslation();
  const [actionType, setActionType] = useState<'note' | 'bookmark' | null>(null);
  const [noteText, setNoteText] = useState('');
  const [selectedColor, setSelectedColor] = useState<HighlightColor>('#FFEB3B' as HighlightColor);
  const [bookmarkNote, setBookmarkNote] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      dialogRef.current?.focus();
    } else if (previousFocusRef.current) {
      previousFocusRef.current.focus?.();
    }
  }, [isOpen]);

  const handleAddNote = () => {
    if (onAddNote && noteText.trim()) {
      onAddNote(noteText.trim(), selectedColor);
      setNoteText('');
      setActionType(null);
      onClose();
    }
  };

  const handleAddBookmark = async () => {
    if (onAddBookmark) {
      await onAddBookmark(currentLocation, bookmarkNote.trim() || undefined);
      setBookmarkNote('');
      setActionType(null);
      onClose();
    }
  };

  const handleClose = () => {
    setActionType(null);
    setNoteText('');
    setBookmarkNote('');
    onClose();
  };

  // Quick Actions content
  const quickActionsContent = (
    <div className="flex-1 overflow-y-auto p-6">
      {!actionType && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-neutral-600 mb-2">
            {currentLocationLabel}
          </p>

          {/* Add Note Button */}
          {onAddNote && (
            <motion.button
              onClick={() => setActionType('note')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-4 rounded-2xl
                       bg-gradient-to-br from-primary-500 to-primary-600
                       text-white shadow-lg hover:shadow-xl
                       transition-shadow duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <StickyNote className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">{t('App.Reader.QuickAction.AddPageNoteTitle')}</div>
                <div className="text-sm text-primary-100">{t('App.Reader.QuickAction.AddPageNoteDescription')}</div>
              </div>
            </motion.button>
          )}

          {/* Add Bookmark Button */}
          {onAddBookmark && (
            <motion.button
              onClick={() => setActionType('bookmark')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-4 p-4 rounded-2xl
                       bg-gradient-to-br from-primary-500 to-primary-700
                       text-white shadow-lg hover:shadow-xl
                       transition-shadow duration-200"
            >
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <BookmarkIcon className="w-6 h-6" />
              </div>
              <div className="flex-1 text-left">
                <div className="font-semibold">{t('App.Reader.QuickAction.AddBookmarkTitle')}</div>
                <div className="text-sm text-primary-100">{t('App.Reader.QuickAction.AddBookmarkDescription')}</div>
              </div>
            </motion.button>
          )}
        </div>
      )}

      {/* Add Note Form */}
      {actionType === 'note' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              {t('App.Reader.QuickAction.NoteTextLabel')}
            </label>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder={t('App.Reader.QuickAction.NotePlaceholder')}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-neutral-300
                       focus:outline-none focus:ring-2 focus:ring-primary-500
                       resize-none"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              {t('App.Reader.QuickAction.ColorLabel')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {highlightColors.map(({ color, name }) => (
                <button
                  key={color}
                  onClick={() => setSelectedColor(color)}
                  aria-label={t('App.Reader.QuickAction.SelectColorAriaLabel', { color: name })}
                  aria-pressed={selectedColor === color}
                  className={`w-10 h-10 rounded-lg border-2 transition-all
                           ${selectedColor === color ? 'border-neutral-900 scale-110' : 'border-neutral-300'}`}
                  style={{ backgroundColor: color }}
                  title={name}
                />
              ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setActionType(null)}
              className="flex-1 px-6 py-3 rounded-xl bg-neutral-200 hover:bg-neutral-300
                       text-neutral-900 font-medium transition-colors"
            >
              {t('App.Reader.QuickAction.BackButton')}
            </button>
            <button
              onClick={handleAddNote}
              disabled={!noteText.trim()}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600
                       text-white font-medium shadow-lg hover:shadow-xl
                       disabled:opacity-50 disabled:cursor-not-allowed
                       transition-all duration-200"
            >
              {t('App.Reader.QuickAction.AddNoteButton')}
            </button>
          </div>
        </div>
      )}

      {/* Add Bookmark Form */}
      {actionType === 'bookmark' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              {t('App.Reader.QuickAction.BookmarkNoteLabel')}
            </label>
            <textarea
              value={bookmarkNote}
              onChange={(e) => setBookmarkNote(e.target.value)}
              placeholder={t('App.Reader.QuickAction.BookmarkNotePlaceholder')}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-neutral-300
                       focus:outline-none focus:ring-2 focus:ring-primary-500
                       resize-none"
              autoFocus
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setActionType(null)}
              className="flex-1 px-6 py-3 rounded-xl bg-neutral-200 hover:bg-neutral-300
                       text-neutral-900 font-medium transition-colors"
            >
              {t('App.Reader.QuickAction.BackButton')}
            </button>
            <button
              onClick={handleAddBookmark}
              className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700
                       text-white font-medium shadow-lg hover:shadow-xl
                       transition-all duration-200"
            >
              {t('App.Reader.QuickAction.AddBookmarkButton')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // If rendering as a panel, return content directly
  if (asPanel) {
    return quickActionsContent;
  }

  // Otherwise, wrap in bottom sheet
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[1100]"
          />

          {/* Bottom Sheet */}
          <motion.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="quickaction-title"
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') handleClose(); }}
            initial={{ y: '100%', scale: 0.95 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: '100%', scale: 0.95 }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[1100]
                     bg-white/95 backdrop-blur-xl rounded-t-[32px] shadow-2xl
                     border-t border-x border-neutral-200/60
                     flex flex-col overflow-hidden"
            style={{ maxHeight: '85vh' }}
          >
            {/* Drag Handle */}
            <div className="flex justify-center py-3">
              <div className="w-12 h-1.5 bg-neutral-300 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 pb-4 border-b border-neutral-200/60">
              <h3 id="quickaction-title" className="text-lg font-semibold text-neutral-900">
                {actionType === 'note' ? t('App.Reader.QuickAction.AddPageNoteTitle') : actionType === 'bookmark' ? t('App.Reader.QuickAction.AddBookmarkTitle') : t('App.Reader.QuickAction.Title')}
              </h3>
              <motion.button
                onClick={handleClose}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                className="w-10 h-10 rounded-full flex items-center justify-center
                         bg-neutral-100/80 hover:bg-neutral-200/80 transition-colors duration-200"
                aria-label={t('App.Reader.Highlight.CloseLabel')}
              >
                <X className="w-5 h-5 text-neutral-600" aria-hidden="true" />
              </motion.button>
            </div>

            {/* Content */}
            {quickActionsContent}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
